import React, { useState, useEffect, useMemo } from 'react';
import Modal2 from 'components/_shared/Modal2';
import MemberStatus from './components/MemberStatus';
import Button from '@mui/material/Button';
import { makeStyles } from '@mui/styles';
import { Deployments, Homeless, MemberSearchResult } from 'types';
import { LoadingCircle } from 'components/_shared/LoadingCircle';
import { searchForMember } from 'api/homelesses';
import { IntakeMemberSearchResultHelper, IntakeMemberSearchResultType } from 'util/membershipHelper';

const useStyles = makeStyles(() => ({
    body: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '32px'
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginTop: '40px'
    },
    closeButton: {
        color: '#7378E8',
        fontSize: '14px',
        fontWeight: '600',
        textTransform: 'none !important'
    },
    btnDiv: {
        display: 'flex',
        justifyContent: 'center'
    },
    loadingContainer: {
        padding: '40px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px'
    }
}));

const iconStyle = {
    backgroundColor: '#E7EFFE',
    border: '2px solid #062D7A',
    color: '#062D7A',
    fontSize: '24px'
};

type ProfileEligibilityCheckProps = {
    member: Homeless;
    selectedDeployment: Deployments;
    closeButtonText: string;
    handleEligible: (member: MemberSearchResult) => void;
    handleCloseClick: () => void;
};

const ProfileEligibilityCheck: React.FC<ProfileEligibilityCheckProps> = ({
    member,
    selectedDeployment,
    closeButtonText,
    handleCloseClick,
    handleEligible
}) => {
    const classes = useStyles();
    const [loading, setLoading] = useState(true);
    const [isEligible, setIsEligible] = useState<boolean | null>(null);

    // Build search terms and values from member data (same pattern as Form.tsx)
    const { searchTerms, searchValues } = useMemo(() => {
        const terms: string[] = [];
        const values: string[] = [];

        //not using member_id/insuranceID for this because likely different insurance

        // using name and DOB to check eligibility
        if (member.first_name && member.last_name && member.birthday) {
            terms.push('firstName', 'lastName', 'birthday');
            values.push(
                member.first_name,
                member.last_name,
                member.birthday.split('T')[0] // Format date if needed
            );
        }

        return { searchTerms: terms, searchValues: values };
    }, [member]);

    useEffect(() => {
        const checkEligibility = async () => {
            try {
                setLoading(true);
                const res = await searchForMember(searchTerms, searchValues);
                
                // Handle both array and object response formats
                const samaritanMembers = Array.isArray(res) ? res : res.samaritan_members || [];
                const intakeMemberSearchResult = IntakeMemberSearchResultHelper.getIntakeMemberSearchResultType(
                    samaritanMembers,
                    selectedDeployment
                );
                //if member is eligible (targetOrEligible type) and in the selected deployment, handle eligible
                if (intakeMemberSearchResult.type === IntakeMemberSearchResultType.targetOrEligible && intakeMemberSearchResult.memberResult?.deployment_id === selectedDeployment.id) {
                    handleEligible(intakeMemberSearchResult.memberResult!);
                } else {
                    setIsEligible(false); // Member Not Eligible
                }
            } catch (error) {
                console.error('Error checking eligibility:', error);
                handleCloseClick();
            } finally {
                setLoading(false);
            }
        };

        if (searchTerms.length > 0) {
            checkEligibility();
        } else {
            setLoading(false);
            setIsEligible(false);
        }
    }, [searchTerms, searchValues, selectedDeployment, handleCloseClick, handleEligible]);

    const memberName = member?.pii_first_name && member?.pii_last_name ? `${member.pii_first_name} ${member.pii_last_name}` : member?.name || '-';

    if (loading) {
        return (
            <Modal2
                onClose={handleCloseClick}
                additionalStyles={{ iconStyle: iconStyle }}
                iconName=""
                title="Profile Eligibility Check"
            >
                <div className={classes.loadingContainer}>
                    <LoadingCircle />
                </div>
            </Modal2>
        );
    }

    if (isEligible === false) {
        return (
            <Modal2
                onClose={handleCloseClick}
                additionalStyles={{ iconStyle: iconStyle }}
                iconName=""
                title="Profile Eligibility Check"
            >
                <div className={classes.body}>
                    <MemberStatus
                        photo={member.photo}
                        icon="far fa-exclamation-circle"
                        title={`${memberName} is not eligible for membership.`}
                        subtext={`${memberName} is currently not eligible for membership with ${selectedDeployment.name}. If you believe this is a mistake, contact our support team.`}
                        colorType="yellow"
                    />
                    <div className={classes.btnDiv}>
                        <Button className={classes.closeButton} onClick={handleCloseClick}>
                            {closeButtonText}
                        </Button>
                    </div>
                </div>
            </Modal2>
        );
    }

    return null;
};

export default ProfileEligibilityCheck;
